// PURPOSE: Left panel container with tab navigation
// TABS: Profit & Loss, Trade Evaluation, Executions, Tags

import React, { useState } from 'react';
import { Tabs } from 'antd';
import PropTypes from 'prop-types';

import {
    CheckSquareOutlined,
    DollarOutlined,
    TagsOutlined,
    UnorderedListOutlined
} from '@ant-design/icons';

import TradeEvaluation from '../../trade-evaluation';
import TradeTags from '../../trade-tags';

import ExecutionsTab from './ExecutionsTab';
import ProfitLossTab from './ProfitLossTab';

import './styles.scss';

export default function LeftPanel({
    trade,
    tradeId,
    profitTargetValue,
    stopLossValue,
    initialTarget,
    tradeRisk,
    plannedR,
    realizedR,
    onProfitTargetChange,
    onStopLossChange,
    onCalculateExcursions,
    isCalculatingExcursions,
    isViewAsActive,
    token,
    timezone
}) {
    const [activeTab, setActiveTab] = useState('pnl');

    const tabItems = [
        {
            key: 'pnl',
            label: (
                <span>
                    <DollarOutlined />
                    P&L
                </span>
            ),
            children: (
                <ProfitLossTab
                    trade={trade}
                    profitTargetValue={profitTargetValue}
                    stopLossValue={stopLossValue}
                    initialTarget={initialTarget}
                    tradeRisk={tradeRisk}
                    plannedR={plannedR}
                    realizedR={realizedR}
                    onProfitTargetChange={onProfitTargetChange}
                    onStopLossChange={onStopLossChange}
                    onCalculateExcursions={onCalculateExcursions}
                    isCalculatingExcursions={isCalculatingExcursions}
                    isViewAsActive={isViewAsActive}
                />
            )
        },
        {
            key: 'evaluation',
            label: (
                <span>
                    <CheckSquareOutlined />
                    Evaluation
                </span>
            ),
            children: (
                <TradeEvaluation
                    tradeId={tradeId}
                    initialEvaluation={trade.tradeEvaluation}
                    disabled={isViewAsActive}
                />
            )
        },
        {
            key: 'executions',
            label: (
                <span>
                    <UnorderedListOutlined />
                    Executions
                </span>
            ),
            children: <ExecutionsTab executions={trade.executions} timezone={timezone} />
        },
        {
            key: 'tags',
            label: (
                <span>
                    <TagsOutlined />
                    Tags
                </span>
            ),
            children: (
                <TradeTags
                    tradeId={tradeId}
                    initialTags={trade.tags}
                    token={token}
                    tradeStatus={trade.status}
                />
            )
        }
    ];

    return (
        <div className="left-panel-container">
            <Tabs
                activeKey={activeTab}
                onChange={setActiveTab}
                items={tabItems}
                className="left-panel-tabs"
                tabPosition="top"
                size="small"
            />
        </div>
    );
}

LeftPanel.propTypes = {
    trade: PropTypes.object.isRequired,
    tradeId: PropTypes.string.isRequired,
    profitTargetValue: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    stopLossValue: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    initialTarget: PropTypes.string,
    tradeRisk: PropTypes.string,
    plannedR: PropTypes.string,
    realizedR: PropTypes.string,
    onProfitTargetChange: PropTypes.func.isRequired,
    onStopLossChange: PropTypes.func.isRequired,
    onCalculateExcursions: PropTypes.func.isRequired,
    isCalculatingExcursions: PropTypes.bool,
    isViewAsActive: PropTypes.bool,
    token: PropTypes.string,
    timezone: PropTypes.string
};
